/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity display does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.onChangeCallbacks = [];

		// add the ".is-hidden" class to the type selector by default
		this.pict.MetaTemplate.addPattern('{~ShowTypeSelector:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpDataObject = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			// if the value is set to false, we should hide the title (this is visible by default if not set)
			return (tmpDataObject?.PictForm?.InputExtra?.includes('Show Type Selector')) ? '' : 'is-hidden';
		});
	}

	getPhysicalAssetSelectInputHTMLID(pInputHTMLID)
	{
		return `#SELECT-FOR-${pInputHTMLID}-Asset`;
	}

	getAssetTypeSelectInput(pInputHTMLID)
	{
		return `#SELECT-FOR-${pInputHTMLID}-Type`;
	}

	getPhysicalAssetHiddenInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}-Asset`;
	}

	getAssetTypeHiddenInput(pInputHTMLID)
	{
		return `#${pInputHTMLID}-Type`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#PHYSICALASSET-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#PHYSICALASSET-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Adds an onChange callback to the input handler.
	 * @param {function} pCallback The callback function.
	 */
	addOnChangeCallback(pCallback)
	{
		if (!pCallback || typeof pCallback !== 'function')
		{
			this.pict.log.error('Invalid onChangeCallback provided.');
			return;
		}
		// check if the callback is already in the list
		if (this.onChangeCallbacks.indexOf(pCallback) > -1)
		{
			this.pict.log.warn('onChangeCallback already exists in the list.');
			return;
		}
		this.onChangeCallbacks.push(pCallback);
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} pInput The input object.
	 */
	addLoadingSkeleton(pInput)
	{
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${pInput.Macro.RawHTMLID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID))[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.getElement(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID))[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
		this.pict.ContentAssignment.addClass(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} pInput The input object.
	 */
	removeLoadingSkeleton(pInput)
	{
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${pInput.Macro.RawHTMLID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
		this.pict.ContentAssignment.removeClass(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 * @param {object} pInput The input object.
	 * @param {number} pTypeFilter The state fitler string.
	 */
	initializeSelect2(pInput, pTypeFilter)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpAssetTypeSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('AssetType',
				{
					Input: pInput,
					SearchFields: [ 'AssetNumber', 'Name' ],
					AllowClear: false,
					SearchFallbackPlaceholder: 'Select an Asset Type...',
				});
			// if the select2 plugin is not already initialized, initialize it now for the correct input type
			if (!window['jQuery'](this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID)).data('select2'))
			{
				window['jQuery'](this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID)).select2(tmpAssetTypeSelectOptions);
			}

			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('PhysicalAsset',
				{
					Input: pInput,
					SearchFields: [ 'AssetNumber', 'Name' ],
					AllowClear: false,
					SearchFallbackPlaceholder: `Select a Physical Asset by name or code...`,
					HandleSearchFilters: (pFilters) =>
					{
						if (!pTypeFilter)
						{
							return pFilters;
						}
						pFilters.push(`FBV~IDAssetType~EQ~${pTypeFilter}`);

						return pFilters;
					},
					GetRecordUserFacingID: (pPhysicalasset) => pPhysicalasset.AssetNumber || 'N/A',
				});
			window['jQuery'](this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID)).select2(tmpSelectOptions);
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.addLoadingSkeleton(pInput);
		const rawHTMLID = pInput.Macro.RawHTMLID;
		const assetHiddenInput = $(this.getPhysicalAssetHiddenInputHTMLID(rawHTMLID));
		const assetTypeHiddenInput = $(this.getAssetTypeHiddenInput(rawHTMLID));
		assetHiddenInput.attr('data-i-datum', `${pInput.DataAddress}.Asset`);
		assetTypeHiddenInput.attr('data-i-datum', `${pInput.DataAddress}.AssetType`);
		if (pInput.Default)
		{
			this.assetType = pInput.Default;
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
	{
		if (this.assetType && !pValue.AssetType)
		{
			const assetTypes = await this.pict.providers.PhysicalAssetProvider.getPhysicalAssetTypeByName(this.pict.AppData?.CurrentUser?.CustomerID, this.assetType);
			if (assetTypes && assetTypes.length > 0)
			{
				pValue.AssetType = assetTypes[0].IDAssetType;
			}
		}

		const tmpAssetInputSelectElements = this.pict.ContentAssignment.getElement(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID));
		const tmpAssetInputAssetTypeSelectElements = this.pict.ContentAssignment.getElement(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID));
		if ((!tmpAssetInputSelectElements || tmpAssetInputSelectElements.length < 1) && (!tmpAssetInputAssetTypeSelectElements || tmpAssetInputAssetTypeSelectElements.length < 1))
		{
			return false;
		}

		await this.updateDataFields(pInput, pValue);

		// get the configuration for the current user to filter the Physicalasset search results (by State, etc.)
		this.pict.providers.ConfigurationProvider.getConfigurationByScope(this.pict.AppData?.CurrentUser?.CustomerID, async (pConfig) =>
		{
			let assetType;
			if (Number.isNaN(parseInt(this.assetType)))
			{
				assetType = await this.pict.providers.PhysicalAssetProvider.getPhysicalAssetTypeByName(this.pict.AppData?.CurrentUser?.CustomerID, this.assetType);
			}
			else
			{
				assetType = this.assetType ? await this.pict.providers.PhysicalAssetProvider.getAssetTypeByID(this.assetType) : null;
			}
			this.initializeSelect2(pInput, assetType?.IDAssetType);
			this.removeLoadingSkeleton(pInput);
		});
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		let tmpInputSelectAssetValue = $(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID)).val();
		const tmpInputSelectAssetTypeValue = $(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID)).val();
		if (pValue.AssetType != tmpInputSelectAssetTypeValue)
		{
			tmpInputSelectAssetValue = '';
		}
		const tmpInputValues = {
			AssetType: tmpInputSelectAssetTypeValue,
			Asset: tmpInputSelectAssetValue,
		};
		this.pict.ContentAssignment.assignContent(this.getPhysicalAssetHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpInputSelectAssetValue.toString());
		this.pict.ContentAssignment.assignContent(this.getAssetTypeHiddenInput(pInput.Macro.RawHTMLID), tmpInputSelectAssetTypeValue.toString());
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputValues, pHTMLSelector);
	}

	async updateDataFields(pInput, pValue)
	{
		// if we have a value (ID), go fetch the Physicalasset for display (Name, etc.)
		if (pValue && (pValue.hasOwnProperty('Asset') || pValue.hasOwnProperty('AssetType')))
		{
			const assetType = pValue.AssetType;
			const asset = pValue.Asset;
			// get the value of the selected Physicalasset and set it as the selected Physicalasset on the data object
			const tmpPhysicalasset = asset ? await this.pict.providers.PhysicalAssetProvider.getPhysicalAssetByID(asset) : null;
			// build the prefilled value for the select2 dropdown
			const tmpOption = document.createElement('option');
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getPhysicalAssetSelectInputHTMLID(pInput.Macro.RawHTMLID))[0];
			if (!tmpSelectElement)
			{
				return false;
			}

			if (tmpPhysicalasset?.IDAssetType == assetType)
			{
				tmpOption.value = tmpPhysicalasset.IDPhysicalAsset;
				tmpOption.text = `${tmpPhysicalasset.AssetNumber || 'N/A'}|~|${tmpPhysicalasset.Name || ('Unnamed Physicalasset #' + this.pict.AppData.SelectedPhysicalasset.IDPhysicalasset)}`;
				tmpOption.selected = true;
				tmpSelectElement.appendChild(tmpOption);
			}
			else
			{
				tmpOption.value = ``;
				tmpOption.text = ``;
				tmpOption.selected = true;
				tmpSelectElement.appendChild(tmpOption);
				this.pict.ContentAssignment.assignContent(this.getPhysicalAssetHiddenInputHTMLID(pInput.Macro.RawHTMLID), '');
			}

			// get the value of the selected Physicalasset and set it as the selected Physicalasset on the data object
			const tmpPhysicalAssetType = assetType ? await this.pict.providers.PhysicalAssetProvider.getAssetTypeByID(assetType) : null;
			// build the prefilled value for the select2 dropdown
			const tmpAssetTypeOption = document.createElement('option');
			const tmpAssetTypeSelectElement = this.pict.ContentAssignment.getElement(this.getAssetTypeSelectInput(pInput.Macro.RawHTMLID))[0];
			if (!tmpAssetTypeSelectElement)
			{
				tmpOption.value = ``;
				tmpOption.text = ``;
				tmpOption.selected = true;
				tmpSelectElement.appendChild(tmpOption);
				this.pict.ContentAssignment.assignContent(this.getAssetTypeHiddenInput(pInput.Macro.RawHTMLID), '');
			}
			else
			{
				tmpAssetTypeOption.value = tmpPhysicalAssetType.IDAssetType;
				tmpAssetTypeOption.text = `${tmpPhysicalAssetType.Name}`;
				tmpAssetTypeOption.selected = true;
				tmpAssetTypeSelectElement.appendChild(tmpAssetTypeOption);
			}

			this.assetType = assetType;
		}
		else
		{
			this.pict.log.info('No Physicalasset selected.');
		}
	}

	/**
	 * Handles the data change event for a select input in the PictProviderInputSelect class.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (this.onChangeCallbacks.length > 0)
		{
			this.onChangeCallbacks.forEach((pCallback) =>
			{
				pCallback(pView, pInput, pValue, pHTMLSelector);
			});
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}
}

module.exports = CustomInputHandler;
